import type { BearbeitungStand } from "./pdfBearbeitung";

/**
 * Ein gemeinsamer Rückgängig-Stapel für den PDF-Editor (Plan 02.10.2026, PR 3).
 *
 * Zwei Welten in einer Reihenfolge: PDF.js hat seinen eigenen Befehlsstapel
 * (Text hinzufügen, Zeichnen, Markieren, Stempel) und kennt Strg+Z nur, solange
 * eines seiner Werkzeuge aktiv ist. Entfernen, Ausschneiden, Text bearbeiten
 * und Drehen laufen außerhalb (Reducer `pdfBearbeitung.ts`, `drehung`) und
 * hatten gar kein Rückgängig. Dieser Stapel merkt sich nur, WAS als Nächstes
 * zurückzunehmen ist — ein PDF.js-Schritt (delegiert an dessen `undo()`) oder
 * ein Schnappschuss von außen. Der Editor wendet die Einträge an.
 *
 * Schnappschüsse tragen beim Merken nur `vorher`; `nachher` wird erst beim
 * Zurücknehmen aus dem dann aktuellen Stand gefüllt. So kommt beim Wiederholen
 * genau das zurück, was da war — samt der Bilder, die ein Ausschnitt erst
 * nach seinem Anlegen bekommen hat.
 *
 * Rein und ohne React, damit die Reihenfolge direkt testbar ist.
 */
export type RueckgaengigEintrag =
    | { art: "pdfjs" }
    | { art: "stand"; vorher: BearbeitungStand; nachher?: BearbeitungStand }
    | { art: "drehung"; vorher: number; nachher?: number };

export class RueckgaengigStapel {
    private undo: RueckgaengigEintrag[] = [];
    private redo: RueckgaengigEintrag[] = [];

    /** Ein neuer Schritt — alles, was wiederholbar war, verfällt. */
    merke(eintrag: RueckgaengigEintrag): void {
        this.undo.push(eintrag);
        this.redo = [];
    }

    kannRueckgaengig(): boolean {
        return this.undo.length > 0;
    }

    kannWiederholen(): boolean {
        return this.redo.length > 0;
    }

    /**
     * Den jüngsten Schritt zurücknehmen. `jetzt` liefert den aktuellen Stand
     * (für `nachher`), damit Wiederholen ihn wiederherstellen kann.
     */
    rueckgaengig(jetzt: { stand: BearbeitungStand; drehung: number }): RueckgaengigEintrag | null {
        const e = this.undo.pop();
        if (!e) return null;
        const voll: RueckgaengigEintrag =
            e.art === "stand" ? { ...e, nachher: jetzt.stand } : e.art === "drehung" ? { ...e, nachher: jetzt.drehung } : e;
        this.redo.push(voll);
        return voll;
    }

    wiederholen(): RueckgaengigEintrag | null {
        const e = this.redo.pop();
        if (!e) return null;
        this.undo.push(e);
        return e;
    }

    /** Nach Speichern oder Seitenumbau: Rückgängig gilt ab hier neu. */
    leeren(): void {
        this.undo = [];
        this.redo = [];
    }

    /**
     * PDF.js hat selbst zurückgenommen (z. B. per eigenem Tastendruck) — der
     * jüngste PDF.js-Eintrag ist damit erledigt, sonst nähme der nächste Strg+Z
     * einen Schritt zu viel.
     */
    pdfjsHatZurueckgenommen(): void {
        for (let i = this.undo.length - 1; i >= 0; i--) {
            if (this.undo[i].art === "pdfjs") {
                this.redo.push(...this.undo.splice(i, 1));
                return;
            }
        }
    }
}

/** Strg+Z / Strg+Y / Strg+Umschalt+Z — und ob der Tastendruck in ein Eingabefeld ging. */
export function tastenkuerzel(e: KeyboardEvent): "rueckgaengig" | "wiederholen" | null {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
    const ziel = e.target as HTMLElement | null;
    if (ziel && typeof ziel.closest === "function") {
        // Im Eingabefeld und im Freitext-Editor von PDF.js (contenteditable) gilt
        // das Rückgängig des Feldes selbst.
        if (ziel.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])")) return null;
    }
    const k = e.key.toLowerCase();
    if (k === "z" && !e.shiftKey) return "rueckgaengig";
    if (k === "y" || (k === "z" && e.shiftKey)) return "wiederholen";
    return null;
}
